import React, { useState, useEffect } from "react";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
  X,
  Loader2,
  RefreshCw,
  Trash2,
  Package,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  generatePropertyExcelTemplate,
  parsePropertyExcel,
  mapExcelRowToProperty,
  validatePropertyRow,
  geocodeAddress,
  getRowValue,
  safeParseNumber,
} from "@/utils/excelUtils";
import { 
  createPropertiesBulk, 
  deleteAllProperties, 
  getImportPackages, 
  importPropertyPackage 
} from "@/api/properties";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function BulkUploadPage() {
  const navigate = useNavigate();
  
  // Excel State
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<number, string[]>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [geocodingProgress, setGeocodingProgress] = useState<{ current: number; total: number } | null>(null);

  // Package State
  const [packages, setPackages] = useState<any[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [isImportingPackage, setIsImportingPackage] = useState(false);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    setIsLoadingPackages(true);
    try {
      const data = await getImportPackages();
      setPackages(data.packages);
    } catch (error) {
      console.error("Error al cargar paquetes:", error);
      toast.error("No se pudieron cargar los paquetes de importación");
    } finally {
      setIsLoadingPackages(false);
    }
  };

  const handleDownloadTemplate = () => {
    const blob = generatePropertyExcelTemplate();
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "plantilla_propiedades.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Plantilla descargada con éxito");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isExcel = 
      selectedFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || 
      selectedFile.type === "application/vnd.ms-excel" ||
      selectedFile.name.endsWith(".xlsx") || 
      selectedFile.name.endsWith(".xls");

    if (!isExcel) {
      toast.error("Por favor, sube un archivo Excel válido (.xlsx o .xls)");
      return;
    }

    setFile(selectedFile);
    setIsProcessing(true);

    try {
      const data = await parsePropertyExcel(selectedFile);
      setPreviewData(data);

      const errors: Record<number, string[]> = {};
      data.forEach((row, index) => {
        const rowErrors = validatePropertyRow(row);
        if (rowErrors.length > 0) {
          errors[index] = rowErrors;
        }
      });
      setValidationErrors(errors);

      if (Object.keys(errors).length > 0) {
        toast.warning(`Se encontraron errores en ${Object.keys(errors).length} filas`);
      } else {
        toast.success(`${data.length} propiedades listas para procesar ubicación`);
        
        // Iniciar geocodificación automática
        await processGeocoding(data);
      }
    } catch (error) {
      console.error("Error al procesar el archivo:", error);
      toast.error("Error al procesar el archivo Excel");
    } finally {
      setIsProcessing(false);
    }
  };

  const processGeocoding = async (data: any[]) => {
    const rowsToGeocode = data.filter(row => !row.lat || !row.lng);
    if (rowsToGeocode.length === 0) return;

    setGeocodingProgress({ current: 0, total: rowsToGeocode.length });
    
    const updatedData = [...data];
    let completed = 0;

    for (let i = 0; i < updatedData.length; i++) {
      const row = updatedData[i];
      if (!row.lat || !row.lng) {
        // Retraso de 1 segundo entre peticiones para respetar la política de Nominatim
        if (completed > 0) await new Promise(resolve => setTimeout(resolve, 1000));
        
        const coords = await geocodeAddress(row.address, row.country || 'Chile');
        if (coords) {
          updatedData[i] = { ...row, lat: coords.lat, lng: coords.lng };
          setPreviewData([...updatedData]);
        }
        completed++;
        setGeocodingProgress({ current: completed, total: rowsToGeocode.length });
      }
    }

    setGeocodingProgress(null);
    toast.success("Geocodificación completada");
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreviewData([]);
    setValidationErrors({});
  };

  const handleConfirmUpload = async () => {
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Por favor, corrige los errores antes de continuar");
      return;
    }

    setIsUploading(true);
    try {
      const propertiesToCreate = previewData.map(mapExcelRowToProperty);
      const result = await createPropertiesBulk(propertiesToCreate);
      
      toast.success(result.message);
      navigate("/dashboard/admin/properties");
    } catch (error: any) {
      console.error("Error en carga masiva:", error);
      toast.error(error.response?.data?.error || "Error al realizar la carga masiva");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const result = await deleteAllProperties();
      toast.success(result.message);
    } catch (error: any) {
      console.error("Error al eliminar propiedades:", error);
      toast.error(error.response?.data?.error || "Error al eliminar las propiedades");
    }
  };

  const handleImportPackage = async (packagePath: string) => {
    setIsImportingPackage(true);
    try {
      const result = await importPropertyPackage(packagePath);
      toast.success(result.message);
      navigate("/dashboard/admin/properties");
    } catch (error: any) {
      console.error("Error al importar paquete:", error);
      toast.error(error.response?.data?.error || "Error al importar el paquete");
    } finally {
      setIsImportingPackage(false);
    }
  };

  const hasErrors = Object.keys(validationErrors).length > 0;

  return (
    <DashboardLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Carga Masiva de Propiedades</h1>
          
          <div className="flex gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="flex items-center gap-2">
                  <Trash2 className="h-4 w-4" />
                  Eliminar Todas las Propiedades
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Estás completamente seguro?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción eliminará TODAS las propiedades de la base de datos de forma permanente.
                    Esta acción no se puede deshacer.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteAll} className="bg-red-600 hover:bg-red-700">
                    Sí, eliminar todo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <Button variant="outline" onClick={handleDownloadTemplate} className="flex items-center gap-2">
              <Download className="h-4 w-4" />
              Descargar Plantilla Excel
            </Button>
          </div>
        </div>

        <Tabs defaultValue="excel" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="excel" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Archivo Excel
            </TabsTrigger>
            <TabsTrigger value="package" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Paquete JSON
            </TabsTrigger>
          </TabsList>

          <TabsContent value="excel" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="md:col-span-1">
                <CardHeader>
                  <CardTitle>Instrucciones</CardTitle>
                  <CardDescription>Sigue estos pasos para publicar tus propiedades masivamente</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">1</div>
                    <p className="text-sm text-gray-600">Descarga la plantilla Excel oficial.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">2</div>
                    <p className="text-sm text-gray-600">Completa los datos de tus propiedades siguiendo el ejemplo.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">3</div>
                    <p className="text-sm text-gray-600">Sube el archivo Excel para validar los datos.</p>
                  </div>
                  <div className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">4</div>
                    <p className="text-sm text-gray-600">Revisa la vista previa y confirma la publicación.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Subir Archivo</CardTitle>
                  <CardDescription>Sube tu archivo Excel con la información de las propiedades</CardDescription>
                </CardHeader>
                <CardContent>
                  {!file ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-12 flex flex-col items-center justify-center space-y-4 hover:border-primary/50 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Upload className="h-6 w-6" />
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-medium">Haz clic o arrastra un archivo Excel aquí</p>
                        <p className="text-xs text-gray-500">Solo archivos .xlsx o .xls (Máximo 10MB)</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg border border-primary/20">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded bg-white flex items-center justify-center border">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{file.name}</p>
                          <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={handleRemoveFile}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {previewData.length > 0 && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Vista Previa ({previewData.length} propiedades)</CardTitle>
                    <CardDescription>Revisa los datos antes de confirmar la publicación</CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    {geocodingProgress && (
                      <div className="flex items-center gap-2 text-primary text-sm font-medium animate-pulse">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Buscando coordenadas: {geocodingProgress.current}/{geocodingProgress.total}
                      </div>
                    )}
                    {hasErrors && (
                      <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
                        <AlertCircle className="h-4 w-4" />
                        {Object.keys(validationErrors).length} filas con errores
                      </div>
                    )}
                    <Button 
                      disabled={hasErrors || isUploading || isProcessing} 
                      onClick={handleConfirmUpload}
                      className="flex items-center gap-2"
                    >
                      {isUploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Publicando...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="h-4 w-4" />
                          Confirmar Publicación
                        </>
                      )}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">Fila</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead>Título</TableHead>
                          <TableHead>Precio</TableHead>
                          <TableHead>m²</TableHead>
                          <TableHead>Operación</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Ubicación (Lat/Lng)</TableHead>
                          <TableHead>Dirección</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.map((row, index) => (
                          <TableRow key={index} className={validationErrors[index] ? "bg-red-50" : ""}>
                            <TableCell className="text-xs text-gray-500">{index + 1}</TableCell>
                            <TableCell>
                              {validationErrors[index] ? (
                                <div className="group relative">
                                  <AlertCircle className="h-5 w-5 text-red-500 cursor-help" />
                                  <div className="absolute left-6 top-0 hidden group-hover:block bg-white border shadow-lg rounded-md p-2 z-10 w-[200px] text-xs text-red-600">
                                    {validationErrors[index].map((err, i) => (
                                      <div key={i}>• {err}</div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <CheckCircle2 className="h-5 w-5 text-green-500" />
                              )}
                            </TableCell>
                            <TableCell className="font-medium truncate max-w-[200px]" title={row.title}>
                              {getRowValue(row, ['title', 'titulo']) || 'Sin título'}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const rawPrice = getRowValue(row, ['price', 'precio', 'valor']);
                                const parsedPrice = safeParseNumber(rawPrice);
                                return parsedPrice > 0 
                                  ? `$${parsedPrice.toLocaleString()}` 
                                  : <span className="text-red-500 font-medium">No definido</span>;
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const area = getRowValue(row, ['area_m2', 'area', 'metros']);
                                const parsedArea = safeParseNumber(area);
                                return parsedArea > 0 ? `${parsedArea} m²` : 'N/A';
                              })()}
                            </TableCell>
                            <TableCell className="capitalize">
                              {getRowValue(row, ['type', 'tipo_operacion']) || row.type || '-'}
                            </TableCell>
                            <TableCell className="capitalize">
                              {getRowValue(row, ['propertytype', 'tipo_propiedad']) || row.propertyType || '-'}
                            </TableCell>
                            <TableCell className="text-xs">
                              {row.lat && row.lng ? (
                                <span className="text-green-600 font-medium">
                                  {Number(row.lat).toFixed(4)}, {Number(row.lng).toFixed(4)}
                                </span>
                              ) : (
                                <span className="text-amber-600 italic">Buscando...</span>
                              )}
                            </TableCell>
                            <TableCell className="truncate max-w-[200px]" title={getRowValue(row, ['address', 'direccion'])}>
                              {getRowValue(row, ['address', 'direccion']) || row.address || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="package" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Paquetes de Importación Local</CardTitle>
                    <CardDescription>
                      Importa propiedades directamente desde carpetas en el servidor (incluye imágenes locales)
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchPackages} disabled={isLoadingPackages}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingPackages ? 'animate-spin' : ''}`} />
                    Actualizar
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingPackages ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <p className="text-sm text-gray-500">Buscando paquetes en el servidor...</p>
                  </div>
                ) : packages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
                    <Package className="h-12 w-12 text-gray-300 mb-2" />
                    <p className="text-gray-500 font-medium">No se encontraron paquetes</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Asegúrate de que existan carpetas en public/Propiedades masivas/ con un archivo propiedades.json
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {packages.map((pkg) => (
                      <div 
                        key={pkg.id}
                        className="flex items-center justify-between p-4 bg-white border rounded-lg hover:border-primary/50 transition-colors shadow-sm"
                      >
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-lg bg-primary/5 flex items-center justify-center">
                            <Package className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{pkg.name}</h3>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span className="flex items-center gap-1">
                                <FileText className="h-3 w-3" />
                                {pkg.propertyCount} propiedades
                              </span>
                              <span>•</span>
                              <span>{new Date(pkg.createdAt).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        
                        <Button 
                          disabled={isImportingPackage}
                          onClick={() => handleImportPackage(pkg.path)}
                          className="flex items-center gap-2"
                        >
                          {isImportingPackage ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Importando...
                            </>
                          ) : (
                            <>
                              Importar Paquete
                              <ArrowRight className="h-4 w-4" />
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertCircle className="h-5 w-5" />
                  <CardTitle className="text-lg">Información Importante</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="text-sm text-amber-700 space-y-2">
                <p>
                  La importación de paquetes procesará todas las imágenes locales encontradas en las carpetas y las subirá a GridFS automáticamente.
                </p>
                <p>
                  Este proceso puede tardar varios minutos dependiendo de la cantidad de propiedades y el tamaño de las imágenes.
                </p>
                <ul className="list-disc list-inside ml-2 mt-2 space-y-1">
                  <li>Se mapearán automáticamente los campos de propiedades.json</li>
                  <li>Se intentará geocodificar las direcciones que no tengan lat/lng</li>
                  <li>Las propiedades se asignarán al usuario administrador actual</li>
                </ul>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
